import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Breadcrumbs: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const getBreadcrumbs = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs = [];

    // Always start with Home
    breadcrumbs.push({
      label: t('nav.home'),
      path: '/',
      icon: Home,
      isHome: true
    });

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      
      // Skip 'app' segment in breadcrumbs
      if (segment === 'app') return;
      
      // Map segments to readable labels
      const labelMap: Record<string, string> = {
        'dashboard': t('nav.dashboard'),
        'calendar': t('nav.calendar'),
        'ask-doctor': 'Ask a Doctor',
        'profile': t('nav.profile'),
        'auth': 'Authentication',
        'login': t('nav.login'),
        'register': t('nav.register')
      };

      const label = labelMap[segment] || segment.charAt(0).toUpperCase() + segment.slice(1).replace('-', ' ');
      
      breadcrumbs.push({
        label,
        path: currentPath,
        isLast: index === pathSegments.length - 1
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  // Don't show breadcrumbs on home page
  if (location.pathname === '/') {
    return null;
  }

  return (
    <motion.nav
      className="flex items-center space-x-2 text-sm text-gray-600 mb-6"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {breadcrumbs.map((crumb, index) => (
        <motion.div
          key={crumb.path}
          className="flex items-center space-x-2"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 }}
        >
          {index > 0 && (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          )}
          
          {crumb.isLast ? (
            <span className="text-purple-600 font-medium flex items-center space-x-1">
              {crumb.icon && <crumb.icon className="w-4 h-4" />}
              <span>{crumb.label}</span>
            </span>
          ) : (
            <Link
              to={crumb.path}
              className="hover:text-purple-600 transition-colors flex items-center space-x-1"
            >
              {crumb.icon && <crumb.icon className="w-4 h-4" />}
              <span>{crumb.label}</span>
            </Link>
          )}
        </motion.div>
      ))}
    </motion.nav>
  );
};

export default Breadcrumbs;